import { Box, useTheme } from "@mui/material";
import { DataGrid, DataGridProps } from "@mui/x-data-grid";
import {
  getDataGridStyles,
  defaultDataGridProps,
  DEFAULT_DATA_GRID_PAGE_SIZE,
} from "utils/dataGridStyles";

interface CustomDataGridProps extends Omit<DataGridProps, "sx"> {
  /**
   * Minimum height for the DataGrid container
   * @default 150
   */
  minHeight?: number;
  /**
   * Custom styles to apply to the DataGrid
   */
  sx?: DataGridProps["sx"];

  rowsPosition?: "absolute" | "relative";
}

function CustomDataGrid({
  rows = [],
  minHeight = 150,
  sx,
  rowsPosition = "absolute",
  ...dataGridProps
}: CustomDataGridProps) {
  const theme = useTheme();

  // Get the page size from props or default
  const pageSize =
    dataGridProps.initialState?.pagination?.paginationModel?.pageSize ||
    DEFAULT_DATA_GRID_PAGE_SIZE;

  // Calculate dynamic height based on visible rows per page (not total rows)
  const visibleRows = Math.min(rows.length, pageSize);
  const dynamicHeight = Math.max(
    minHeight,
    56 + // Header height
      visibleRows * 52 + // Row height * visible rows per page
      (rows.length > pageSize ? 52 : 0) + // Pagination footer height (only if needed)
      20 // Extra padding
  );

  return (
    <Box sx={{ flex: 1, position: "relative", height: dynamicHeight }}>
      <Box sx={{ position: rowsPosition, inset: 0 }}>
        <DataGrid
          rows={rows}
          sx={{
            ...getDataGridStyles(theme),
            ...sx,
          }}
          {...defaultDataGridProps}
          {...dataGridProps}
        />
      </Box>
    </Box>
  );
}

export default CustomDataGrid;
